import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/bionexus/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";

// Minimal local typing for the beta supabase.auth.oauth namespace.
type OAuthApi = {
  getAuthorizationDetails: (id: string) => Promise<{ data: any; error: any }>;
  approveAuthorization: (id: string) => Promise<{ data: any; error: any }>;
  denyAuthorization: (id: string) => Promise<{ data: any; error: any }>;
};
const oauth = (supabase.auth as unknown as { oauth: OAuthApi }).oauth;

export default function OAuthConsent() {
  const [params] = useSearchParams();
  const authorizationId = params.get("authorization_id") ?? "";
  const [details, setDetails] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!authorizationId) {
        setError("Missing authorization_id");
        return;
      }
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        const next = window.location.pathname + window.location.search;
        window.location.href = "/auth?next=" + encodeURIComponent(next);
        return;
      }
      try {
        const { data, error } = await oauth.getAuthorizationDetails(authorizationId);
        if (!active) return;
        if (error) {
          setError(error.message);
          return;
        }
        const immediate = data?.redirect_url ?? data?.redirect_to;
        if (immediate && !data?.client) {
          window.location.href = immediate;
          return;
        }
        setDetails(data);
      } catch (e: any) {
        setError(e?.message ?? "Failed to load authorization");
      }
    })();
    return () => {
      active = false;
    };
  }, [authorizationId]);

  async function decide(approve: boolean) {
    setBusy(true);
    const { data, error } = approve
      ? await oauth.approveAuthorization(authorizationId)
      : await oauth.denyAuthorization(authorizationId);
    if (error) {
      setBusy(false);
      setError(error.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError("No redirect returned by the authorization server.");
      return;
    }
    window.location.href = target;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center pt-24 px-6">
        <div className="w-full max-w-lg border border-border bg-card p-8">
          {error ? (
            <>
              <h1 className="font-display font-medium text-2xl mb-2">Authorization error</h1>
              <p className="text-sm text-destructive">{error}</p>
            </>
          ) : !details ? (
            <p className="font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground">
              Loading…
            </p>
          ) : (
            <>
              <p className="font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground mb-2">
                Connect application
              </p>
              <h1 className="font-display font-medium text-2xl mb-4">
                {details.client?.name ?? "An external app"} → BioNexus
              </h1>
              <p className="text-sm text-muted-foreground mb-2">
                This lets <strong>{details.client?.name ?? "the client"}</strong> use BioNexus tools while signed in as you.
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                It can browse the materials & applications catalog and submit new
                materials to the review queue. It cannot bypass this app's
                permissions — admin-only actions still require an admin account.
              </p>
              <div className="flex gap-3">
                <Button disabled={busy} onClick={() => decide(true)}>
                  Approve
                </Button>
                <Button disabled={busy} variant="outline" onClick={() => decide(false)}>
                  Cancel connection
                </Button>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
